/* eslint-disable import/no-default-export */
// eslint-disable-next-line no-restricted-imports
import { normalizeBoard } from 'app/gamma/src/api/normalizers/board';
// eslint-disable-next-line no-restricted-imports
import { normalizeOrganization } from 'app/gamma/src/api/normalizers/organization';

// eslint-disable-next-line no-restricted-imports
import type { TeamModel } from 'app/gamma/src/types/models';
// eslint-disable-next-line no-restricted-imports
import type { Action } from '@trello/redux';
// eslint-disable-next-line no-restricted-imports
import { actionCreator, createReducer } from '@trello/redux';

// Action types
// eslint-disable-next-line no-restricted-imports
import type { LoadBoardSuccessAction } from 'app/gamma/src/modules/loaders/load-board';
// eslint-disable-next-line no-restricted-imports
import { LOAD_BOARD_SUCCESS } from 'app/gamma/src/modules/loaders/load-board';
// eslint-disable-next-line no-restricted-imports
import type { LoadHeaderSuccessAction } from 'app/gamma/src/modules/loaders/load-header';
// eslint-disable-next-line no-restricted-imports
import { LOAD_HEADER_SUCCESS } from 'app/gamma/src/modules/loaders/load-header';
import type { LoadMyBoardsSuccessAction } from 'app/src/components/ReduxSearch/loadBoardsForSearch';
import { LOAD_MY_BOARDS_SUCCESS } from 'app/src/components/ReduxSearch/loadBoardsForSearch';
// eslint-disable-next-line no-restricted-imports
import type { LoadTeamSuccessAction } from 'app/gamma/src/modules/loaders/load-team';
// eslint-disable-next-line no-restricted-imports
import { LOAD_TEAM_SUCCESS } from 'app/gamma/src/modules/loaders/load-team';

// eslint-disable-next-line no-restricted-imports
import type { SocketOrganizationAction } from 'app/gamma/src/modules/sockets';
// eslint-disable-next-line no-restricted-imports
import { SOCKET_ORGANIZATION } from 'app/gamma/src/modules/sockets';

// eslint-disable-next-line no-restricted-imports
import type { SetBoardClosedAction } from 'app/gamma/src/modules/state/models/boards';
// eslint-disable-next-line no-restricted-imports
import { SET_BOARD_CLOSED } from 'app/gamma/src/modules/state/models/boards';
// eslint-disable-next-line no-restricted-imports
import type {
  BoardResponse,
  LimitStatus,
  OrganizationsResponse,
} from 'app/gamma/src/types/responses';
// Keys of a type T that can't have something of type U assigned to them
// KeysWithoutType<{a:string, b?:string, c:number}, undefined> returns 'a' | 'c'
// KeysWithoutType<{a:string, b:string, c:number}, string> returns 'c'
type KeysWithoutType<T, U> = {
  [K in keyof T]-?: U extends T[K] ? never : K;
}[keyof T];
// This is a subset of T that includes keys that are in K or
// which cannot be undefined
type HasRequiredKeys<T, K extends keyof T> = { [key in K]-?: T[key] } &
  { [key in KeysWithoutType<T, undefined>]: T[key] };
import updateModelList from './util/updateModelList';

export const CREATE_TEAM_SUCCESS = Symbol('models/CREATE_TEAM_SUCCESS');
export const onCreateTeamSuccess = actionCreator(CREATE_TEAM_SUCCESS);

export type CreateTeamSuccessAction = Action<
  typeof CREATE_TEAM_SUCCESS,
  OrganizationsResponse
>;

export type TeamState = TeamModel[];

// Reducer
const initialState: TeamState = [];

const getOrganizationsFromBoards = (boards: BoardResponse[]) => {
  return boards
    .filter(
      (board): board is HasRequiredKeys<BoardResponse, 'organization'> =>
        !!board.organization,
    )
    .map((board) => board.organization as NonNullable<OrganizationsResponse>);
};

const reduceOrganizationsPayload = (
  state: TeamModel[],
  organizations?: OrganizationsResponse | OrganizationsResponse[],
  boards?: BoardResponse[],
) => {
  if (!organizations) {
    return state;
  }

  // seed the state map with the current state
  const stateMap = new Map<string, TeamModel>(
    state.map<[string, TeamModel]>((team) => [team.id, team]),
  );

  // include any organizations that come from boards
  // NOTE: dropping these in first in case the organizations response
  //       has more fully populated organization properties
  if (boards) {
    getOrganizationsFromBoards(boards).forEach((organization) => {
      stateMap.set(
        organization.id,
        normalizeOrganization(organization, stateMap.get(organization.id)),
      );
    });
  }

  // if it's not an array of orgs, we are either modifying or adding one team
  if (!Array.isArray(organizations)) {
    stateMap.set(
      organizations.id,
      normalizeOrganization(organizations, stateMap.get(organizations.id)),
    );
  } else {
    // now include the organizations from the org response array
    organizations.forEach((organization) => {
      stateMap.set(
        organization.id,
        normalizeOrganization(organization, stateMap.get(organization.id)),
      );
    });
  }

  return [...stateMap.values()];
};

const incrementFreeBoardLimitCount = (team: TeamModel): TeamModel => {
  if (
    !team ||
    !team.limits ||
    !team.limits.orgs.freeBoardsPerOrg.count ||
    closed
  ) {
    return team;
  }

  const { count, disableAt, warnAt } = team.limits.orgs.freeBoardsPerOrg;
  const newCount = count + 1;

  let newStatus: LimitStatus = 'ok';
  if (newCount > disableAt) {
    newStatus = 'maxExceeded';
  } else if (newCount === disableAt) {
    newStatus = 'disabled';
  } else if (newCount >= warnAt) {
    newStatus = 'warn';
  }

  return {
    ...team,
    limits: {
      ...team.limits,
      orgs: {
        ...team.limits.orgs,
        freeBoardsPerOrg: {
          ...team.limits.orgs.freeBoardsPerOrg,
          count: newCount,
          status: newStatus,
        },
      },
    },
  };
};

export default createReducer(initialState, {
  [LOAD_MY_BOARDS_SUCCESS](
    state: TeamState,
    { payload }: LoadMyBoardsSuccessAction,
  ) {
    const organizations = reduceOrganizationsPayload(
      state,
      payload.organizations,
      payload.boards,
    );

    return organizations;
  },

  [LOAD_BOARD_SUCCESS](state: TeamState, { payload }: LoadBoardSuccessAction) {
    return reduceOrganizationsPayload(state, payload.organization ?? undefined);
  },

  [LOAD_TEAM_SUCCESS](state: TeamState, { payload }: LoadTeamSuccessAction) {
    return reduceOrganizationsPayload(state, payload);
  },

  [LOAD_HEADER_SUCCESS](
    state: TeamState,
    { payload }: LoadHeaderSuccessAction,
  ) {
    return reduceOrganizationsPayload(state, payload.organizations);
  },

  [CREATE_TEAM_SUCCESS](
    state: TeamState,
    { payload }: CreateTeamSuccessAction,
  ) {
    return updateModelList(state, payload, normalizeOrganization);
  },
  [SET_BOARD_CLOSED](state: TeamState, { payload }: SetBoardClosedAction) {
    const { closed, idOrganization } = normalizeBoard(payload);

    return state.map((team) =>
      !closed && team.id === idOrganization
        ? incrementFreeBoardLimitCount(team)
        : team,
    );
  },

  [SOCKET_ORGANIZATION](
    state: TeamState,
    { payload }: SocketOrganizationAction,
  ) {
    return updateModelList(state, payload, normalizeOrganization);
  },
});
